// The HistoryState class is a service that provides the history state.
// It can be used to get the current state, and to update the state.
// It also provides an observable that emits when the state might have changed.
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import type { Observable } from 'rxjs';
import { Subject, fromEvent, map, merge, startWith } from 'rxjs';
import type { HistoryUrl } from './history-url';
import { provideHistoryUrl } from './history-url';

export interface HistoryStateContract {
  getState(): Observable<Record<string, any>>;

  stateMightChanged(): void;

  mergeCurrentState(state: Record<string, any>): void;

  back(): void;

  goBack(n: number): void;
}

export class HistoryState implements HistoryStateContract {
  private _historyStateChanged$: Subject<void> = new Subject<void>();

  constructor(private _historyUrl: HistoryUrl) {}

  // The getState method returns an observable that emits the current state.
  public getState(): Observable<Record<string, any>> {
    return merge(this._historyStateChanged$, fromEvent(window, 'popstate')).pipe(
      startWith(undefined),
      map(() => window.history.state)
    );
  }

  // The stateMightChanged method emits when the state might have changed.
  public stateMightChanged(): void {
    this._historyStateChanged$.next(undefined);
  }

  public mergeCurrentState(state: Record<string, any>): void {
    window.history.replaceState({ ...window.history.state, ...state }, '', window.location.href);
    this.stateMightChanged();
  }

  /** PLEASE USE FROM BCF-LOCATION */
  public back(): void {
    window.history.back();
  }

  /** PLEASE USE FROM BCF-LOCATION */
  public goBack(n: number): void {
    this._historyUrl.routerMightWentBack(n);
    window.history.go(n * -1);
    this._historyUrl.routerMightReplaced();
  }
}

export function provideHistoryState(): HistoryState {
  return provideSingleton(HistoryState, () => new HistoryState(provideHistoryUrl()));
}
